import React, { useEffect, useState } from "react";

import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { MdmDeviceDto, MdmObjectDto, useGetDevicesPagedLazyQuery, useGetDevicesPagedQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import { DataTablePFSEvent } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import MapWrapper from "../components/MapWrapper";
import ObjectAndDeviceTableWrapper from "../components/ObjectAndDeviceTableWrapper";
import ObjectMapContent from "../components/ObjectMapContent";
import useProjectContext from "../hooks/useProjectContext";
import { DEVICES_TABLE_NAME, DEVICE_CONTAINER } from "../utils/const";
import { LazyState, TableColumn } from "../utils/types";
import { errorToaster, handleDeviceStatus, userTableDetails } from "../utils/utils";

const Devices = () => {
  const {projectId, project, userSettings} = useProjectContext();
  const tableDetails = userTableDetails(userSettings, DEVICES_TABLE_NAME);
  const [lazyState, setLazyState] = useState<LazyState>({
    first: 0,
    rows: tableDetails?.rows || 10,
    page: 1,
    sortField: "identifier",
    sortOrder: 1,
    multiSortMeta: [],
    filters: {},
    andFilters: [],
    internalFilters: [],
  });

  const [getFilteredDevicesData,{data: deviceData, loading }] = useGetDevicesPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  useEffect(() => {
    if(!deviceData && !loading) {
      getFilteredDevicesData({
        variables: {
          input: {
            projectId,
            take: lazyState.rows,
            skip: lazyState.first,
          }
        },
      })
    }
  }, [deviceData]);

  const edit = (rowData: MdmDeviceDto) => (
    <Link to={`${rowData.id}`} title="Device Info">
      {rowData.identifier}
    </Link>
  );
  
  const handleLastMeasurementDate = (rowData: MdmDeviceDto) =>
    rowData.status.lastMeasurementDate &&
    parseToLocalZoneDateTime(rowData.status.lastMeasurementDate).format(DEFAULT_DATE_FORMATTER);

  const objectCodeTemplate = (rowData: MdmDeviceDto) => (
    <Link to={`/mdm/${projectId}/objects/${rowData.objectRelation?.mdmObject?.id}`}>
      {rowData.objectRelation?.mdmObject?.identifier}
    </Link>
  );
  const tableData = (deviceData?.mdmDevicesByProjectIdPaged.items || []) as MdmDeviceDto[];

  const measurementKeys = [...project?.mdmProjectById.deviceConfigs?.reduce((acc, curr) => {
    if (curr?.measurementMappings) {
      curr.measurementMappings.forEach(m => acc.add(m?.key!));
    }
    return acc;
  }, new Set<string>()) ?? [] ];

  const renderDatasetVal = (rowData: MdmDeviceDto, key: string) => {
    const datasetId = rowData?.boundDatasets?.mapped.find(m => m?.mapping === key)?.dataset?.identifier;
    return datasetId ? <DatasetVal datasetId={datasetId} /> : "-"
  }
  const columns: TableColumn<MdmDeviceDto>[] = [
    {
      field: "identifier",
      header: "Identifier",
      body: edit,
      sortable: true,
      columnKey: "identifier",
    },
    {
      field: "objectRelation.mdmObject.identifier",
      header: "Object Code",
      body: objectCodeTemplate,
      sortable: true,
      columnKey: "Object Code",
    },
    {
      field: "objectRelation.mdmObject.address.fullAddress",
      header: "Object Address",
      sortable: true,
      columnKey: "Object Address",
    },
    {
      field: "objectRelation.roleInObject",
      header: "Type",
      body: rowData =>
        project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.find(
          type => type?.id === rowData.objectRelation?.roleInObject
        )?.title ?? "",
      sortable: true,
      columnKey: "Type",
    },
    {
      field: "config.title",
      header: "Config",
      sortable: true,
      columnKey: "Config",
    },
    {
      field: "status.status",
      header: "Status",
      body: handleDeviceStatus,
      sortable: true,
      hideInFile: true,
      columnKey: "Status",
    },
    {
      field: "datasetCount",
      header: "Datasets",
      sortable: true,
    },
    {
      field: "status.lastMeasurementDate",
      header: "Last Measurement Date",
      body: handleLastMeasurementDate,
      sortable: true,
      columnKey: "Last Measurement Date",
    },
    ...measurementKeys.map(key => ({
      header: key,
      body: (data: MdmDeviceDto) => renderDatasetVal(data, key),
      columnKey: key,
    })),
  ];



  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header="Table">
        <ObjectAndDeviceTableWrapper
          columns={columns}
          data={tableData}
          containerName={DEVICE_CONTAINER}
          tableName={DEVICES_TABLE_NAME}
          loading={loading}
          onLazyEvent={setLazyState}
          lazyState={lazyState}
          totalItems={deviceData?.mdmDevicesByProjectIdPaged.pageInfo?.total || 0}
          getQueryData={getFilteredDevicesData}
        />
      </TabPanel>
      <TabPanel header="Map">
        <DeviceProvider lazyState={lazyState} records={deviceData?.mdmDevicesByProjectIdPaged.pageInfo?.total ?? 100} />
      </TabPanel>
    </TabView>
  );
};

export default Devices;

type DeviceProviderProps = {
  lazyState: DataTablePFSEvent;
  records: number;
};
const DeviceProvider: React.FC<DeviceProviderProps> = ({lazyState,records}) => {
  const {projectId} = useProjectContext();

  const { data, loading } = useGetDevicesPagedQuery({
    variables: {
      input: {
        projectId,
        skip: 0,
        take: records,
        andFilters: lazyState.andFilters,
      },
    },
  });

  return (
    <MapWrapper>
      {loading && <ProgressSpinner className='mt-8 w-full' style={{zIndex: 400}}/>}
      <ObjectMapContent objects={[...new Set(data?.mdmDevicesByProjectIdPaged.items.map(t => t?.objectRelation?.mdmObject! as MdmObjectDto))]} />
    </MapWrapper>
  );
}

const DatasetVal: React.FC<{datasetId: string}> = ({datasetId}) => {
  const { data, isLoading } = useGetRecentData([datasetId], {count: 1});
  return <>
    {isLoading && <Button icon="pi pi-spin pi-spinner" className="p-button-text" loading={isLoading} />}
    {!isLoading && data?.[0].data[0].value.toFixed(2)}
  </>
}